import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, ArrowRight, Download } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { API, LINKS } from "@/lib/site";

const inputCls =
  "bg-transparent border-0 border-b border-ink/25 rounded-none h-12 px-0 text-ink placeholder:text-ink font-light focus-visible:ring-0 focus-visible:border-flame text-base";

export const PlaybookGate = ({
  triggerClassName,
  label = "Free $1M Sales Playbook",
  showArrow = true,
  showDownloadIcon = false,
  dataTestId = "playbook-trigger",
}) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "playbook", message: "$1M Playbook download" });
      toast.success("Enjoy the playbook - opening it now.");
      window.open(LINKS.playbook, "_blank", "noopener,noreferrer");
      setForm({ name: "", email: "" });
      setOpen(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" className={triggerClassName} data-testid={dataTestId}>
          {showDownloadIcon && (
            <Download size={18} strokeWidth={2.25} className="shrink-0 transition-transform duration-200 group-hover:-translate-y-0.5" />
          )}
          <span className="text-center leading-none">{label}</span>
          {showArrow && (
            <ArrowUpRight
              size={18}
              className="shrink-0 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          )}
        </button>
      </DialogTrigger>
      <DialogContent
        className="bg-paper border border-ink text-ink w-[calc(100%-32px)] max-h-[calc(100dvh-32px)] overflow-y-auto sm:max-w-md rounded-none p-6 pt-12 sm:p-8 [&>button]:min-h-[44px] [&>button]:min-w-[44px] [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:right-1 [&>button]:top-1"
        data-testid="playbook-dialog"
      >
        <DialogHeader className="space-y-0 text-left">
          <p className="font-mono text-base font-medium uppercase tracking-[0.12em] text-flame mb-3 text-pretty">Free download</p>
          <DialogTitle className="font-display text-3xl font-semibold tracking-tight leading-[1.05] text-ink">
            The $1M Sales <span className="font-editorial italic font-light">Playbook.</span>
          </DialogTitle>
          <DialogDescription className="text-ink font-light text-base pt-3">
            How we find buyers, book meetings and close deals. Enter your details to open the playbook.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-5 space-y-5" noValidate data-testid="playbook-form">
          <Input
            placeholder="Your name"
            value={form.name}
            onChange={update("name")}
            className={inputCls}
            data-testid="playbook-name"
          />
          <Input
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={update("email")}
            className={inputCls}
            data-testid="playbook-email"
          />
          <button
            type="submit"
            disabled={loading}
            className="group w-full inline-flex items-center justify-center gap-2 bg-cobalt px-8 py-4 font-mono text-base font-medium uppercase tracking-[0.12em] text-white border border-cobalt transition-all duration-200 hover:bg-cobalt-hover active:scale-95 disabled:opacity-60"
            data-testid="playbook-submit"
          >
            {loading ? "Sending..." : "Get the playbook"}
            <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
          </button>
          <p className="text-center font-mono text-base uppercase tracking-[0.08em] text-ink text-pretty">
            No spam · Unsubscribe anytime
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
